import React, { useRef, useCallback } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { isSavage } from '~/shared/utils/brandNameHelper';
import getStockStatus, { StockStatus } from '~/shared/utils/getStockStatus';
import { shouldUseAlternateSizeGuide } from '~/shared/utils/shouldUseAlternateSizeGuide';
import {
  AttributeFieldTypes,
  GenericProductAttributeSelector,
  ProductAttributeSelector,
  useProductAttributeFieldType,
  useProductContext,
  useProductDetailSelectionState,
  useErrorRegistrationContext,
  useId,
  useTheme,
  desktop,
  mobile,
  useProductDetailContext,
} from '~/techstyle-shared/react-components';
import BraSizeAttributeSelector from '~/techstyle-shared/react-components/src/BraSizeAttributeSelector';
import { isBandCupBraSizes } from '~/techstyle-shared/react-components/src/utils/sizes';
import {
  FormattedMessage,
  FormattedHTMLMessage,
  useIntl,
  useRegion,
} from '~/techstyle-shared/react-intl';

import ProductDetailFitCallout from '../ProductDetailFitCallout';
import { ProductDetailLabel } from '../ProductDetailLabels';
import ScrubsSizeGuideModal from '../ScrubsSizeGuideModal';

const listStyle = css`
  margin: 0;
  gap: 6px;
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(var(--selectorMinSize, 80px), var(--columnOptions, 1fr))
  );
`;

const itemStyle = css`
  padding: 0;
  /* temporary fix for sizing - shared component complexity */
  > div {
    flex-grow: 1;
  }
`;

const SizeSelector = styled(GenericProductAttributeSelector)`
  width: 100%;
  margin-bottom: 16px;

  --columnOptions: ${({ $numberOfOptions }) =>
    $numberOfOptions > 4 ? '1fr' : 'min-content'};
  --selectorMinSize: ${({ $hasLongLabels }) =>
    $hasLongLabels ? '100px' : '80px'};
  ${mobile`
    --columnOptions: 3fr;
  `} ${desktop`
    margin-bottom: 8px;
    input {
      cursor: pointer;
    }
  `};
`;

const selectedLabelTextStyle = css`
  ${({ theme }) => theme.selectorStyles.selectedLabelTextStyle};
`;

const SelectorTextWrapper = styled.div`
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${({ theme }) => theme.colors.sublabelBlack};
  font-size: 14px;
  font-weight: 600;
`;

const SelectOptionLabelWrapper = styled.span`
  display: flex;
  padding-top: 3px;
  margin: 6px 0;
`;

const SelectSizeWarningLabel = styled.div`
  color: ${({ theme }) => theme.colors.textDeemphasized};
  line-height: 1.14;
  letter-spacing: ${-0.25 / 14}em;
  font-weight: 600;
  margin-right: 2px;
`;

const SelectSizeWarningMessage = styled.span`
  color: ${({ theme }) => theme.colors.errorMessage};
  line-height: 1.14;
  letter-spacing: ${-0.25 / 14}em;
  text-transform: none;
  font-weight: 600;
  margin-left: 8px;
`;

const ScarcityNotice = styled.span`
  color: ${({ theme }) => theme.colors.errorMessage};
  font-size: 14px;
  margin-left: 8px;
`;

const SoldOutMessage = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.errorMessage};
  display: flex;
`;

const SizeLabelWrapper = styled.span`
  display: flex;
  > div {
    display: flex;
    align-items: center;
  }
`;

const ScarcityMessage = () => {
  const { selectedSKU } = useProductDetailSelectionState();
  const region = useRegion();
  if (!selectedSKU) {
    return null;
  }

  const stockStatus = getStockStatus({ ...selectedSKU, region });

  if (stockStatus === StockStatus.OK) {
    return null;
  }

  return (
    <>
      <ScarcityNotice data-autotag="pdp-product-callout">
        {stockStatus === StockStatus.LOW_STOCK && (
          <FormattedMessage
            id="site_product.scarcity_banner_low_stock"
            defaultMessage="Low Stock"
          />
        )}
        {stockStatus === StockStatus.CRITICAL && (
          <FormattedMessage
            id="site_product.scarcity_banner_only_n_left"
            defaultMessage="Only {1} left!"
            values={{ 1: selectedSKU?.availableQuantity }}
          />
        )}
        {stockStatus === StockStatus?.URGENT && (
          <FormattedMessage
            id="site_product.scarcity_banner_urgent_only_n_left"
            defaultMessage="Hurry up! Only {1} left!"
            values={{ 1: selectedSKU.availableQuantity }}
          />
        )}
      </ScarcityNotice>
    </>
  );
};
const SizeLabel = ProductDetailLabel;
const scrubsId = config.get('public.productCategories.scrubs');

const CupSummary = () => {
  return (
    <>
      <SelectorTextWrapper>
        <FormattedMessage
          id="site_product_page.cup_size"
          defaultMessage="Cup Size"
        />
      </SelectorTextWrapper>
    </>
  );
};

const BandSummary = ({
  attribute,
  selectedOption,
  sizeField,
  productDetailFromCtx,
}) => {
  return (
    <>
      <SizeSummary
        attribute={attribute}
        selectedOption={selectedOption}
        field={sizeField}
        productDetail={productDetailFromCtx}
      />
      <SelectorTextWrapper>
        <FormattedMessage
          id="site_product_page.band_size"
          defaultMessage="Band Size"
        />
      </SelectorTextWrapper>
    </>
  );
};

BandSummary.propTypes = {
  attribute: PropTypes.object,
  productDetailFromCtx: PropTypes.object,
  selectedOption: PropTypes.object,
  sizeField: PropTypes.string,
};

const SizeSummary = ({ attribute, selectedOption, field, productDetail }) => {
  const categoryIdList = productDetail.product.productCategoryIdList;
  const tagIdList = productDetail.product.tagIdList;
  const isScrubs = categoryIdList?.includes(scrubsId);
  const { productIndex } = useProductDetailSelectionState();
  const uniqueId = useId(`${productIndex}-${attribute.field}-`);
  const theme = useTheme();
  const { selectorLabelStyle } = theme.selectorStyles;
  const errorRegistrationDisabled = Boolean(
    theme.context?.errorRegistrationDisabled
  );
  const ref = useRef();
  const hasError =
    (selectedOption && !selectedOption.isAvailable) || !selectedOption;
  useErrorRegistrationContext(
    ref,
    errorRegistrationDisabled ? false : hasError,
    uniqueId
  );
  const useAlternateSizeGuide = shouldUseAlternateSizeGuide(tagIdList);

  return (
    <>
      <SelectorTextWrapper ref={ref}>
        {!selectedOption && (
          <SelectOptionLabelWrapper>
            <SelectSizeWarningLabel>
              <FormattedMessage
                id="site_product_page.size"
                defaultMessage="Size:"
              />
            </SelectSizeWarningLabel>
            <SelectSizeWarningMessage>
              <FormattedMessage
                id="site_product_page.select_an_option"
                defaultMessage="Select an option"
              />
            </SelectSizeWarningMessage>
          </SelectOptionLabelWrapper>
        )}
        <SizeLabelWrapper>
          {selectedOption && (
            <div>
              <SizeLabel
                attribute={attribute}
                selectedOption={selectedOption}
                labelTextStyles={selectorLabelStyle}
                infoTextStyles={selectedLabelTextStyle}
                field={field}
              />
              <ScarcityMessage />
            </div>
          )}
          {selectedOption &&
            (!selectedOption.isAvailable ||
              !selectedOption.isAvailableForUser) && (
              <SoldOutMessage data-autotag="pdp-size-sold-out">
                &nbsp;-&nbsp;
                <FormattedMessage
                  id="site_product_page.out_of_stock"
                  defaultMessage="Out of Stock"
                />
              </SoldOutMessage>
            )}
        </SizeLabelWrapper>
        {isScrubs ? (
          <ScrubsSizeGuideModal
            showSizeCalloutMessage
            showMessaging={false}
            categoryIdList={categoryIdList}
            useAlternate={useAlternateSizeGuide}
          />
        ) : (
          <ProductDetailFitCallout />
        )}
      </SelectorTextWrapper>
    </>
  );
};

SizeSummary.propTypes = {
  attribute: PropTypes.object,
  field: PropTypes.string,
  productDetail: PropTypes.object,
  selectedOption: PropTypes.object,
};

// temporary call out for scrubs FLST-11494
const ExtendedSizeCallout = styled.div`
  text-align: center;
  margin-bottom: 12px;
  padding: 6px;
  background-color: ${({ theme }) => theme.colors.pdpExtendedSizeCalloutBg};
`;

const scrubsExtendedSizeId = config.get(
  'public.productTags.scrubsExtendedSize'
);

function ProductDetailSizeSelector(props) {
  const {
    selectorStyles: { itemSizeLabelStyle, itemSoldOutStyle, inputSizeStyle },
  } = useTheme();
  const { formatMessage } = useIntl();
  const ariaPrefix = formatMessage({
    id: 'pdp_attribute_selector.aria_prefix_size',
    defaultMessage: 'size',
  });

  const sizeField = useProductAttributeFieldType(AttributeFieldTypes.SIZE);
  const { productIndex, attributes } = useProductContext();
  const { selectedAttributes } = useProductDetailSelectionState(productIndex);
  const getRadioButtonProps = useCallback((option) => {
    return {
      'data-autotag': option.isAvailable
        ? 'pdp-saleable-size'
        : 'pdp-sold-out-size',
    };
  }, []);

  const selectedOption = selectedAttributes?.[sizeField];
  const isOSFM = selectedOption?.value === 'OSFM';
  const { options } = attributes?.find(
    (attribute) => attribute.field === sizeField
  );
  const onlyOneSize = options?.length === 1;

  const productDetailFromCtx = useProductDetailContext();
  const showScrubsExtendedSizeCallout =
    productDetailFromCtx.product.tagIdList.includes(scrubsExtendedSizeId);

  if (!sizeField || (isOSFM && onlyOneSize)) {
    return null;
  }

  const showBraSizeSelector =
    isSavage() &&
    ['size_bra', 'size'].includes(sizeField) &&
    isBandCupBraSizes(options);

  return (
    <ProductAttributeSelector field={sizeField}>
      {({ attribute, selectedOption }) => {
        const hasLongLabels = options.some((option) => option.label.length > 6);
        return (
          <>
            {showBraSizeSelector ? (
              <BraSizeAttributeSelector
                $numberOfOptions={options?.length}
                $hasLongLabels={hasLongLabels}
                ariaPrefix={ariaPrefix}
                data-autotag="pdp-size-section"
                inputStyle={inputSizeStyle}
                itemLabelStyle={itemSizeLabelStyle}
                itemSoldOutStyle={itemSoldOutStyle}
                renderCupSummary={() => <CupSummary />}
                renderBandSummary={() => (
                  <BandSummary
                    attribute={attribute}
                    selectedOption={selectedOption}
                    sizeField={sizeField}
                    productDetailFromCtx={productDetailFromCtx}
                  />
                )}
                getRadioButtonProps={getRadioButtonProps}
                itemStyle={itemStyle}
                listStyle={listStyle}
                {...props}
              />
            ) : (
              <SizeSelector
                $numberOfOptions={options?.length}
                $hasLongLabels={hasLongLabels}
                ariaPrefix={ariaPrefix}
                data-autotag="pdp-size-section"
                inputStyle={inputSizeStyle}
                itemLabelStyle={itemSizeLabelStyle}
                itemSoldOutStyle={itemSoldOutStyle}
                renderSummary={() => (
                  <SizeSummary
                    attribute={attribute}
                    selectedOption={selectedOption}
                    field={sizeField}
                    productDetail={productDetailFromCtx}
                  />
                )}
                getRadioButtonProps={getRadioButtonProps}
                itemStyle={itemStyle}
                listStyle={listStyle}
                {...props}
              />
            )}
            {showScrubsExtendedSizeCallout && (
              <ExtendedSizeCallout>
                <FormattedHTMLMessage
                  id="scrubs_pdp.extended_sizing_coming_soon"
                  defaultMessage="Sizes <strong>XXL/1X-4X</strong> coming soon!"
                />
              </ExtendedSizeCallout>
            )}
          </>
        );
      }}
    </ProductAttributeSelector>
  );
}

export default ProductDetailSizeSelector;
